/** The Days card's model (plan §5 "Days card", O7 / O10, §10.3 `days/`): the itinerary's days as
 *  the records outline holds them — each day a record of its own (`day:<id>`, a negative id for
 *  a day the draft adds), their order a membership list on the itinerary (`days` on
 *  `itinerary:<group id>`, the day ids in order) — and what the card says of them: each row's
 *  span label ("Day 3–4"), the caption's counts ("8 rows · 12 days"), the check of the span
 *  total against the duration display ("Duration says 12 days"), a description's first line.
 *
 *  Days-only: one outline level (no stops), read over the draft in edit mode and over the
 *  stored values in view mode (`entries` null), so the EDIT half reuses it unchanged. Pure. */
import type { CatalogSource } from '@/api/activities';
import type { ItineraryDay } from '@/api/itineraries';
import type { ItineraryDayRecordFieldKey } from '@/api/itineraryRecord';
import { htmlText } from '@/components/RichText/htmlText';
import { membershipFreshFrom } from '@/features/records/collections/drift';
import type { FreshFrom } from '@/features/records/draft/DraftProvider';
import { membershipPaths } from '@/features/records/collections/membership';
import { recordKey, type RecordKey } from '@/features/records/draft/changeSet';
import { outlineFlatRows, valueIn, type Baselines, type Entries, type OutlineSpec } from '@/features/records/outline/tree';

import { dayRangeLabel, type DayMark, type DayMarks, type DayRange, type RibbonDay } from '../parts/dayRibbonModel';
import { daySpan } from '../parts/itineraryDays';
import { ITINERARY_DAY_ENTITY } from '../registry';

/** The itinerary's day list: the day ids in order (the registry's `days`). */
export const DAYS_PATH = 'days';

/** The itinerary kind's ONE membership set (see `membershipPaths`): its only list is the days'. */
export const ITINERARY_MEMBERSHIPS = membershipPaths([DAYS_PATH]);

const membershipFresh = membershipFreshFrom(ITINERARY_MEMBERSHIPS);

/** An image field's value that is not its wire form: the detail's CDN keys, which the draft's
 *  baselines hold until edit_context brings the Image id (the route map's, a day's image). */
const imageKeysOnly = (path: string, value: unknown) =>
  (path === 'route_map' || path === 'image') && value !== null && typeof value === 'object';

/** The DraftProvider's `freshFrom` for the itinerary: a refetch merges the day list both sides
 *  changed (the server's own `days` merge), and a day deleted on the server turns the draft's
 *  edits of it "deleted elsewhere". An image read from the detail (its CDN keys, not yet its
 *  Image id) is compared with nothing: a restored image change waits for edit_context instead
 *  of turning stale against the detail. Stable (module constant). */
export const ITINERARY_FRESH: FreshFrom = (baselines, entries) =>
  membershipFresh(baselines, entries).filter((f) => !imageKeysOnly(f.path, f.current));

/** A new day's values until edited: untitled, one day long. */
export const NEW_DAY: Readonly<Record<ItineraryDayRecordFieldKey, unknown>> = {
  title: null,
  subtitle: null,
  day_span: 1,
  image: null,
  description: null,
  footer: null,
};

/** The days as an outline: one level, the itinerary's day list. */
export const DAYS_OUTLINE: OutlineSpec = [{ entity: ITINERARY_DAY_ENTITY, list: DAYS_PATH, defaults: NEW_DAY }];

export const dayRec = (id: number): RecordKey => recordKey(ITINERARY_DAY_ENTITY, id);

/** A day's stored values, keyed as its changes are (`day:<id>`). The image is its CDN keys —
 *  the detail does not carry its Image id (the route map's precedent); the EDIT half seeds every
 *  baseline from edit_context instead. */
export function dayBaselineValues(d: ItineraryDay): Record<ItineraryDayRecordFieldKey, unknown> {
  return {
    title: d.title,
    subtitle: d.subtitle,
    day_span: d.day_span,
    image: d.image,
    description: d.description,
    footer: d.footer,
  };
}

/** Every day's record (`"day:<id>"` → field → value). */
export function dayBaselines(days: readonly ItineraryDay[]): Record<RecordKey, Record<string, unknown>> {
  return Object.fromEntries(days.map((d) => [dayRec(d.id), dayBaselineValues(d)]));
}

/** One row of the Days card. */
export interface DayRowModel {
  kind: 'day';
  id: number;
  /** Its place among the days; null for a saved day the draft removed. */
  index: number | null;
  removed: boolean;
  /** The days it covers; null for a removed day (it no longer counts). */
  range: DayRange | null;
  span: number;
  /** Its title, trimmed ('' when untitled). */
  title: string;
}

const textOf = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** The rows as the draft (edit mode) or the stored itinerary (`entries` null) has them, in the
 *  list's order: each covering its span after the one before ("Day 3–4"); a removed day listed
 *  last, counting for nothing. */
export function dayRows(entries: Entries | null, baselines: Baselines, ownerRec: RecordKey): DayRowModel[] {
  let next = 1;
  return outlineFlatRows(DAYS_OUTLINE, entries, baselines, ownerRec).map((r) => {
    const rec = dayRec(r.id);
    const span = daySpan(valueIn(DAYS_OUTLINE, entries, baselines, rec, 'day_span') as number | null);
    let range: DayRange | null = null;
    if (!r.removed) {
      range = { start: next, end: next + span - 1 };
      next = range.end + 1;
    }
    return {
      kind: 'day',
      id: r.id,
      index: r.index,
      removed: r.removed,
      range,
      span,
      title: textOf(valueIn(DAYS_OUTLINE, entries, baselines, rec, 'title')),
    };
  });
}

/** What moves the outline: the rows, their order, their spans and removals. A keystroke in a
 *  title is not in it (the row renders itself). */
export function dayRowsKey(rows: readonly DayRowModel[]): string {
  return rows.map((r) => `${r.id}${r.removed ? 'x' : `@${r.span}`}`).join(',');
}

/** What the ribbon draws: the rows' order, spans and titles. */
export function ribbonKey(rows: readonly DayRowModel[]): string {
  return JSON.stringify(rows.map((r) => [r.id, r.removed, r.span, r.title]));
}

/** "Day 3–4", "Day 5"; "Removed" for a day the draft removed. */
export function dayLabel(range: DayRange | null): string {
  return range ? `Day ${dayRangeLabel(range)}` : 'Removed';
}

/** A row's name, for its controls and its accessible name: "Day 3–4 · Ayutthaya"; a removed
 *  day by its title. */
export function dayRowName(row: Pick<DayRowModel, 'range' | 'title'>): string {
  if (!row.range) return row.title || 'the removed day';
  return [dayLabel(row.range), row.title].filter(Boolean).join(' · ');
}

/** The stored days' names by id — their rows' names in the stored order, spans and titles
 *  ("Day 2–3 · Ayutthaya"): how the ledger and the server's sentences name a saved day. */
export function storedDayNames(baselines: Baselines, ownerRec: RecordKey): Map<number, string> {
  return new Map(dayRows(null, baselines, ownerRec).map((r) => [r.id, dayRowName(r)]));
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/** The trip's length: the kept rows' spans. */
export function totalDays(rows: readonly DayRowModel[]): number {
  return rows.reduce((sum, r) => (r.removed ? sum : sum + r.span), 0);
}

/** The caption's counts: "8 rows · 12 days" (removed days left out); "No days". */
export function daysCaption(rows: readonly DayRowModel[]): string {
  const kept = rows.filter((r) => !r.removed).length;
  return kept === 0 ? 'No days' : `${plural(kept, 'row')} · ${plural(totalDays(rows), 'day')}`;
}

/** "12 days", "10–12 days", "14-day" in the duration display, as day ranges. */
const DURATION_DAYS = /(\d+)(?:\s*[-–]\s*(\d+))?[\s-]*days?\b/gi;

/** The info note under the caption when the span total disagrees with every day count the
 *  duration display names ("Duration says 12 days"); null when it agrees, names none ("2
 *  weeks"), or there are no days. */
export function durationMismatch(durationDisplay: string | null | undefined, total: number): string | null {
  if (!durationDisplay || total <= 0) return null;
  const said: DayRange[] = [];
  for (const m of durationDisplay.matchAll(DURATION_DAYS)) {
    const a = Number(m[1]);
    const b = m[2] === undefined ? a : Number(m[2]);
    said.push({ start: Math.min(a, b), end: Math.max(a, b) });
  }
  const first = said[0];
  if (!first || said.some((r) => total >= r.start && total <= r.end)) return null;
  const words = first.end > first.start ? `${first.start}–${first.end} days` : plural(first.start, 'day');
  return `Duration says ${words}`;
}

/** The first line of a day's description as text (a paragraph, a list item, up to a line
 *  break), for the collapsed row; '' when it has none. */
export function descriptionExcerpt(html: unknown): string {
  if (typeof html !== 'string') return '';
  for (const part of html.split(/<br\s*\/?>|<\/(?:p|li|h[1-6]|div)>/i)) {
    const text = htmlText(part);
    if (text) return text;
  }
  return '';
}

/** The itinerary's feed re-syncs its days, matching them by title (§7, F44: a renamed day is
 *  deleted and re-created) — an import (not an AI import) of a source that updates itineraries.
 *  The band's Source cell says it; edit mode's day rows carry the verdict. */
export function feedMatchesDaysByTitle(source: Pick<CatalogSource, 'ai_import' | 'updates'> | null | undefined): boolean {
  return Boolean(source && !source.ai_import && source.updates);
}

/** The ribbon's days and marks from the rows. View mode marks nothing; the EDIT half passes
 *  its changed / added days (`marks`), and a removed day is marked here, with the days it
 *  covers as stored (`stored`, the stored rows' ranges by id) — its label until the save. */
export function ribbonInput(
  rows: readonly DayRowModel[],
  marks: DayMarks = {},
  stored: ReadonlyMap<number, DayRange> = new Map(),
): { days: RibbonDay[]; marks: DayMarks } {
  const all: Record<number, DayMark> = { ...marks };
  for (const r of rows) if (r.removed) all[r.id] = { state: 'removed' };
  return {
    days: rows.map((r) => {
      const was = r.removed ? stored.get(r.id) : undefined;
      return { id: r.id, title: r.title || null, day_span: r.span, ...(was ? { stored: was } : {}) };
    }),
    marks: all,
  };
}

/** The stored days' ranges by id (the stored itinerary's rows). */
export function storedDayRanges(baselines: Baselines, ownerRec: RecordKey): Map<number, DayRange> {
  return new Map(dayRows(null, baselines, ownerRec).flatMap((r) => (r.range ? [[r.id, r.range] as const] : [])));
}
